import React, { useEffect, useState, useRef } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import api from "@/api";
import { useI18n } from "@/i18n";
import { Bell, X, Check, Trash, ClockCounterClockwise } from "@phosphor-icons/react";

const POLL_MS = 60_000;

/* ---------------------------------------------------------------------------
   <NotificationsBell /> — topbar bell with unread badge + drawer.
   - Polls /notifications/unread-count every 60s.
   - Opens a right-side drawer (desktop) / bottom sheet (mobile).
   - Groups by severity, supports mark-read / dismiss / mark-all-read.
   --------------------------------------------------------------------------- */
export default function NotificationsBell({ inSidebar = false }) {
  const { lang } = useI18n();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [unread, setUnread] = useState(0);
  const [rows, setRows] = useState(null);
  const pollTimer = useRef(null);

  const loadCount = () =>
    api.get("/notifications/unread-count").then((r) => setUnread(r.data.count)).catch(() => {});
  const loadRows = () =>
    api.get("/notifications").then((r) => setRows(r.data.rows)).catch(() => setRows([]));

  useEffect(() => {
    loadCount();
    pollTimer.current = setInterval(loadCount, POLL_MS);
    return () => clearInterval(pollTimer.current);
  }, []);

  useEffect(() => {
    if (open) loadRows();
  }, [open]);

  const onClick = async (n) => {
    if (!n.read_at) {
      await api.post(`/notifications/${n.id}/read`);
      setUnread((u) => Math.max(0, u - 1));
      setRows((rs) => rs && rs.map((x) => x.id === n.id ? { ...x, read_at: new Date().toISOString() } : x));
    }
    if (n.action_link) {
      setOpen(false);
      navigate(n.action_link);
    }
  };
  const onDismiss = async (e, n) => {
    e.stopPropagation();
    await api.delete(`/notifications/${n.id}`);
    setRows((rs) => rs && rs.filter((x) => x.id !== n.id));
    if (!n.read_at) setUnread((u) => Math.max(0, u - 1));
  };
  const markAllRead = async () => {
    await api.post("/notifications/read-all");
    setUnread(0);
    setRows((rs) => rs && rs.map((x) => ({ ...x, read_at: x.read_at || new Date().toISOString() })));
  };

  const labels = lang === "fr" ? {
    title: "Notifications", empty: "Vous êtes à jour 🎉",
    markAll: "Tout marquer lu", noUnread: "Aucune notification non lue",
  } : {
    title: "Notifications", empty: "You're all caught up 🎉",
    markAll: "Mark all read", noUnread: "No unread notifications",
  };

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className={`relative ${inSidebar ? "p-2" : "p-2"} rounded-[8px] hover:bg-[var(--bg-subtle)] transition-colors`}
        aria-label="Open notifications"
        data-testid="notifications-bell"
      >
        <Bell size={18} weight={unread > 0 ? "fill" : "regular"} style={{ color: unread > 0 ? "var(--accent)" : "var(--text-secondary)" }} />
        {unread > 0 && (
          <span
            className="absolute -top-0.5 -right-0.5 text-[9px] font-mono font-bold text-white rounded-full flex items-center justify-center"
            style={{ background: "var(--accent)", minWidth: 16, height: 16, padding: "0 4px" }}
            data-testid="notifications-badge"
          >
            {unread > 99 ? "99+" : unread}
          </span>
        )}
      </button>

      {open && createPortal((
        <>
          <div className="fixed inset-0 z-[1020]" style={{ background: "rgba(0,0,0,0.4)", backdropFilter: "blur(4px)" }} onClick={() => setOpen(false)} />
          <div
            className="fixed z-[1030] flex flex-col top-0 md:top-4 right-0 md:right-4 bottom-0 md:bottom-4 left-0 md:left-auto w-full md:w-[420px] md:rounded-[16px]"
            style={{ background: "var(--bg-elevated)", border: "1px solid var(--border)", boxShadow: "var(--shadow-lg)" }}
            data-testid="notifications-drawer"
          >
            <div className="px-4 py-3 flex items-center justify-between" style={{ borderBottom: "1px solid var(--border)" }}>
              <div className="flex items-center gap-2">
                <Bell size={16} weight="fill" style={{ color: "var(--accent)" }} />
                <div>
                  <div className="text-[15px] font-semibold tracking-tight">{labels.title}</div>
                  {unread > 0 && <div className="text-[10.5px] font-mono" style={{ color: "var(--text-tertiary)" }}>{unread} unread</div>}
                </div>
              </div>
              <div className="flex items-center gap-1">
                {unread > 0 && (
                  <button onClick={markAllRead} className="text-[11.5px] px-2 py-1 rounded-[6px] hover:bg-[var(--bg-subtle)] flex items-center gap-1" data-testid="notifications-mark-all">
                    <Check size={12} /> {labels.markAll}
                  </button>
                )}
                <button onClick={() => setOpen(false)} className="p-1.5 rounded-full hover:bg-[var(--bg-subtle)]" data-testid="notifications-close">
                  <X size={16} />
                </button>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto px-3 py-3 space-y-2">
              {!rows ? (
                <div className="text-[12px] font-mono px-3 py-4" style={{ color: "var(--text-tertiary)" }}>…</div>
              ) : rows.length === 0 ? (
                <div className="text-center py-16">
                  <div className="text-[32px] mb-2">🎉</div>
                  <div className="text-[14px] font-medium">{labels.empty}</div>
                </div>
              ) : (
                rows.map((n) => <NotifCard key={n.id} n={n} onClick={onClick} onDismiss={onDismiss} lang={lang} />)
              )}
            </div>
          </div>
        </>
      ), document.body)}
    </>
  );
}

const SEVERITY_TONE = {
  critical: { color: "var(--accent)", bg: "rgba(237,32,39,0.06)" },
  warning:  { color: "#F59E0B", bg: "rgba(245,158,11,0.08)" },
  success:  { color: "var(--success)", bg: "rgba(52,168,83,0.08)" },
  info:     { color: "var(--info)", bg: "rgba(13,113,221,0.06)" },
};

function NotifCard({ n, onClick, onDismiss, lang }) {
  const tone = SEVERITY_TONE[n.severity] || SEVERITY_TONE.info;
  const unread = !n.read_at;
  const ago = relTime(new Date(n.created_at_iso || n.created_at * 1000), lang);
  const navigate = useNavigate();

  // Pick FR or EN strings — falls back to whichever exists
  const title = (lang === "fr" ? n.title_fr : n.title_en) || n.title_fr || n.title_en || n.title;
  const body = (lang === "fr" ? n.body_fr : n.body_en) || n.body_fr || n.body_en || n.body;

  const runQuickAction = (e, qa) => {
    e.stopPropagation();
    // Mark notif as read silently
    if (unread) {
      api.post(`/notifications/${n.id}/read`).catch(() => {});
    }
    const prefill = qa.prefill || {};
    const target = prefill.target || n.action_link || "/";
    // Pre-fill via query params — pages read them on mount
    const qs = new URLSearchParams();
    Object.entries(prefill).forEach(([k, v]) => {
      if (k === "target" || v == null) return;
      // Pick the right localized note field
      if (k === "notes_fr" || k === "notes_en") {
        if (k === `notes_${lang}`) qs.set("notes", v);
      } else {
        qs.set(k, v);
      }
    });
    const url = target.includes("?") ? `${target}&${qs}` : `${target}${qs.toString() ? "?" + qs : ""}`;
    navigate(url);
  };

  return (
    <div
      className="w-full p-3 rounded-[10px] transition-all"
      style={{
        background: unread ? tone.bg : "var(--bg-elevated)",
        border: `1px solid ${unread ? tone.color : "var(--border)"}`,
        opacity: unread ? 1 : 0.8,
      }}
      data-testid={`notif-${n.id}`}
    >
      <div className="flex items-start justify-between gap-2 cursor-pointer" onClick={() => onClick(n)}>
        <div className="flex-1 min-w-0">
          <div className="flex items-baseline gap-2">
            {unread && <span className="w-1.5 h-1.5 rounded-full shrink-0 mt-1.5" style={{ background: tone.color }} />}
            <div className="text-[13px] font-semibold leading-snug" style={{ color: "var(--text-primary)" }}>
              {title}
            </div>
          </div>
          {body && (
            <div className="text-[12px] mt-1 whitespace-pre-line leading-relaxed" style={{ color: "var(--text-secondary)" }}>
              {body.length > 240 ? body.slice(0, 237) + "…" : body}
            </div>
          )}
          <div className="flex items-center gap-2 mt-2">
            <span className="text-[10px] font-mono uppercase font-bold" style={{ color: tone.color }}>{n.severity}</span>
            <span className="text-[10px] font-mono" style={{ color: "var(--text-tertiary)" }}>· {ago}</span>
            {n.country && <span className="text-[10px] font-mono" style={{ color: "var(--text-tertiary)" }}>· {n.country}</span>}
          </div>
        </div>
        <span
          onClick={(e) => onDismiss(e, n)}
          className="p-1 rounded-full hover:bg-[var(--bg-subtle)] shrink-0"
          data-testid={`notif-dismiss-${n.id}`}
          role="button"
          tabIndex={0}
        >
          <Trash size={12} style={{ color: "var(--text-tertiary)" }} />
        </span>
      </div>

      {/* Quick actions */}
      {Array.isArray(n.quick_actions) && n.quick_actions.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mt-3 pt-2.5" style={{ borderTop: "1px dashed var(--border)" }}>
          {n.quick_actions.map((qa, i) => (
            <button
              key={i}
              onClick={(e) => runQuickAction(e, qa)}
              className="px-2.5 py-1.5 text-[11.5px] font-medium rounded-[8px] hover:opacity-80 transition-opacity"
              style={{ background: tone.color, color: "white" }}
              data-testid={`notif-qa-${n.id}-${i}`}
            >
              {(lang === "fr" ? qa.label_fr : qa.label_en) || qa.label_fr || qa.label_en}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function relTime(date, lang) {
  const diff = (Date.now() - date.getTime()) / 1000;
  if (diff < 60) return lang === "fr" ? "à l'instant" : "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}${lang === "fr" ? " min" : "m"}`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}${lang === "fr" ? " h" : "h"}`;
  if (diff < 86400 * 7) return `${Math.floor(diff / 86400)}${lang === "fr" ? " j" : "d"}`;
  return date.toLocaleDateString();
}
